import { useEffect, useMemo, useRef, useState } from "react";
import { FeatureCollection } from "geojson";
import { MapaDeChuvaFiltroIndice } from "core/features/maps/typings/mapaDeChuva";
import {
  DEFAULT_SURFACE_WIDTH,
  PREVIEW_SURFACE_WIDTH,
  dedupeRainfallStations,
  expandBoundsWithClipPolygons,
  extractRainfallSamples,
  extractTalhaoClipPolygons,
  renderRainfallSurfaceAsync,
  resolveOverlayBounds,
  resolveRainfallBands,
  scheduleIdleTask,
  yieldToMain,
  GeoBounds,
} from "core/features/maps/rainfall-interpolation";

interface UseRainfallInterpolationOverlayParams {
  map: google.maps.Map | null | undefined;
  enabled: boolean;
  points?: FeatureCollection & {
    Properties?: {
      latitudeMinima?: number;
      latitudeMaxima?: number;
      longitudeMinima?: number;
      longitudeMaxima?: number;
    };
  };
  indices?: MapaDeChuvaFiltroIndice[] | null;
  selectedBandCodes?: number[] | null;
}

interface OverlayState {
  url: string;
  bounds: GeoBounds;
}

export function useRainfallInterpolationOverlay({
  map,
  enabled,
  points,
  indices,
  selectedBandCodes,
}: UseRainfallInterpolationOverlayParams) {
  const overlayRef = useRef<google.maps.GroundOverlay | null>(null);
  const overlayUrlRef = useRef<string | null>(null);
  const renderGenerationRef = useRef(0);
  const [surface, setSurface] = useState<OverlayState | null>(null);

  const bands = useMemo(() => resolveRainfallBands(indices), [indices]);

  const stations = useMemo(() => {
    if (!enabled || !points) {
      return [];
    }
    return dedupeRainfallStations(extractRainfallSamples(points, bands));
  }, [enabled, points, bands]);

  const clipPolygons = useMemo(() => {
    if (!enabled || !points) {
      return [];
    }
    return extractTalhaoClipPolygons(points);
  }, [enabled, points]);

  const selectedCodes = useMemo(
    () => selectedBandCodes ?? [],
    [selectedBandCodes],
  );
  const selectedCodesKey = selectedCodes.join(",");

  const propertiesKey = points?.Properties
    ? [
        points.Properties.latitudeMinima,
        points.Properties.latitudeMaxima,
        points.Properties.longitudeMinima,
        points.Properties.longitudeMaxima,
      ].join(":")
    : "";

  useEffect(() => {
    renderGenerationRef.current += 1;
    const generation = renderGenerationRef.current;
    const isCurrent = () => generation === renderGenerationRef.current;

    if (!enabled) {
      setSurface(null);
      return;
    }

    if (!stations.length) {
      console.warn(
        "[rainfall-interpolation] No rainfall samples (stations or talhões) for the selected period.",
      );
      setSurface(null);
      return;
    }

    if (!selectedCodes.length) {
      setSurface(null);
      return;
    }

    const baseBounds = resolveOverlayBounds(stations, points?.Properties);
    if (!baseBounds) {
      console.warn(
        "[rainfall-interpolation] Could not resolve overlay bounds.",
      );
      setSurface(null);
      return;
    }

    const bounds = expandBoundsWithClipPolygons(baseBounds, clipPolygons);
    let cancelled = false;

    const applySurface = (url: string) => {
      if (cancelled || !isCurrent()) {
        return;
      }
      setSurface((prev) => {
        if (
          prev &&
          prev.url === url &&
          prev.bounds.south === bounds.south &&
          prev.bounds.north === bounds.north &&
          prev.bounds.west === bounds.west &&
          prev.bounds.east === bounds.east
        ) {
          return prev;
        }
        return { url, bounds };
      });
    };

    const cancelIdle = scheduleIdleTask(() => {
      void (async () => {
        try {
          const shouldCancel = () => cancelled || !isCurrent();

          const preview = await renderRainfallSurfaceAsync({
            stations,
            bands,
            selectedBandCodes: selectedCodes,
            bounds,
            clipPolygons,
            width: PREVIEW_SURFACE_WIDTH,
            shouldCancel,
          });

          if (preview) {
            applySurface(preview.url);
          }

          await yieldToMain(48);
          if (shouldCancel()) {
            return;
          }

          const full = await renderRainfallSurfaceAsync({
            stations,
            bands,
            selectedBandCodes: selectedCodes,
            bounds,
            clipPolygons,
            width: DEFAULT_SURFACE_WIDTH,
            shouldCancel,
          });

          if (!full) {
            if (!preview && isCurrent() && !cancelled) {
              console.warn(
                "[rainfall-interpolation] Surface render returned empty.",
                {
                  stationCount: stations.length,
                  bandCount: bands.length,
                  selectedCount: selectedCodes.length,
                },
              );
              setSurface(null);
            }
            return;
          }

          applySurface(full.url);
        } catch (error) {
          console.error(
            "[rainfall-interpolation] Unexpected error while rendering surface.",
            error,
          );
          if (isCurrent() && !cancelled) {
            setSurface(null);
          }
        }
      })();
    });

    return () => {
      cancelled = true;
      cancelIdle();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, stations, bands, selectedCodesKey, propertiesKey, clipPolygons]);

  useEffect(() => {
    if (!map || !surface || typeof google === "undefined") {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
        overlayUrlRef.current = null;
      }
      return;
    }

    const latLngBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(surface.bounds.south, surface.bounds.west),
      new google.maps.LatLng(surface.bounds.north, surface.bounds.east),
    );

    if (overlayRef.current && overlayUrlRef.current === surface.url) {
      overlayRef.current.setMap(map);
      return;
    }

    if (overlayRef.current) {
      overlayRef.current.setMap(null);
      overlayRef.current = null;
    }

    overlayRef.current = new google.maps.GroundOverlay(
      surface.url,
      latLngBounds,
      { opacity: 1, clickable: false },
    );
    overlayUrlRef.current = surface.url;
    overlayRef.current.setMap(map);
  }, [map, surface]);

  // Hide the raster overlay during zoom/pan so Maps does not rescale a large
  // PNG every frame; restore on idle. Keeps gestures responsive on Chuvas.
  useEffect(() => {
    if (!map || typeof google === "undefined") {
      return;
    }

    let restoreTimer: ReturnType<typeof setTimeout> | null = null;

    const hideOverlay = () => {
      if (restoreTimer) {
        clearTimeout(restoreTimer);
        restoreTimer = null;
      }
      overlayRef.current?.setOpacity(0);
    };

    const scheduleRestore = () => {
      if (restoreTimer) {
        clearTimeout(restoreTimer);
      }
      restoreTimer = setTimeout(() => {
        restoreTimer = null;
        overlayRef.current?.setOpacity(1);
      }, 80);
    };

    const zoomListener = map.addListener("zoom_changed", hideOverlay);
    const dragListener = map.addListener("dragstart", hideOverlay);
    const idleListener = map.addListener("idle", scheduleRestore);

    return () => {
      if (restoreTimer) {
        clearTimeout(restoreTimer);
      }
      google.maps.event.removeListener(zoomListener);
      google.maps.event.removeListener(dragListener);
      google.maps.event.removeListener(idleListener);
    };
  }, [map]);

  useEffect(() => {
    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
        overlayUrlRef.current = null;
      }
    };
  }, []);

  return {
    hasSurface: !!surface,
    stationCount: stations.length,
  };
}
